import { Box, Badge, Card, CardContent, Chip, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import Notes from "../ScheduleOverview/Notes/Notes";
import { useMemo, useState } from "react";
import { FindAllTicketsQueryFiltersTicketStatusEnum, NoteDto } from "../../api-client";
import useStore from "../../helpers/useStore";
import { TicketsStoreContext } from "../../stores/Ticketing/tickets.provider";
import React from "react";
import { UserContext } from "../../stores/User/User.provider";
import { observer } from "mobx-react";
import { mobiliTheme } from "../../themes/mobiliTheme";
import PatientHistoryTimeline from "./PatientHistory/PatientHistoryTimeline";
import { useTickets } from "../../stores/Ticketing";
import { TicketDto } from "../../api-client";
import TicketsTable from "../Ticketing/TicketsTable";
import TicketDialog from "../Ticketing/TicketDialog";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { usePatientNotes } from "../../stores/Notes/notes.queries";
import { useCreateNote, useUpdateNote } from "../../stores/Notes/notes.mutations";

export const PatientNotesAndRecords = observer(({ patientId }: { patientId: any }) => {
  const UserStore = useStore(UserContext);
  const { user } = UserStore;
  const [selectedTabs, setSelectedTabs] = useState(1);
  // const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [value, setValue] = React.useState("1");

  // Use React Query for patient notes fetching
  const { data: patientNotes = [] } = usePatientNotes(patientId);
  const { mutateAsync: createNote } = useCreateNote();
  const { mutateAsync: updateNote } = useUpdateNote();

  const [selectedTicketId, setSelectedTicketId] = useState<number | undefined>();
  const [showClosed, setShowClosed] = useState(false);

  // All statuses except CLOSED — used when hiding closed tickets
  const openStatuses = Object.values(FindAllTicketsQueryFiltersTicketStatusEnum).filter(
    (s) => s !== FindAllTicketsQueryFiltersTicketStatusEnum.Closed
  );

  const ticketFilters = useMemo(() => ({
    limit: 20,
    patientId,
    ...(!showClosed ? { ticketStatus: openStatuses } : {}),
  }), [patientId, showClosed]);

  const {
    data: ticketsData,
    isFetchingNextPage,
    hasNextPage: hasMore,
    fetchNextPage,
    isLoading: initialLoading,
  } = useTickets({
    filters: ticketFilters,
    enabled: selectedTabs === 2 && !!patientId,
  });

  // Badge query: always fetch open ticket count (even before tab is clicked)
  const { data: badgeTicketsData } = useTickets({
    filters: { limit: 100, patientId, ticketStatus: openStatuses },
    enabled: !!patientId,
  });
  const openTicketCount = badgeTicketsData?.pages?.flat()?.length ?? 0;

  // Flatten pages (safe default empty array)
  const allTickets: TicketDto[] | undefined = ticketsData?.pages.flat();
  // Filter out closed tickets
  const tickets: TicketDto[] | undefined = allTickets;
  // Derive initial loading if hook doesn’t expose isLoading
  const isInitialLoading = !ticketsData && isFetchingNextPage;

  const handleRowClick = (ticket: TicketDto) => {
    setOpenDialog(true);
    setSelectedTicketId(ticket.id);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTicketId(undefined);
  };

  const handleTabChange = (event: any, newValue: any) => {
    setSelectedTabs(newValue);
  };

  const handleUpdateNote = async (index: number, updatedCard: NoteDto) => {
    updatedCard.patientId = patientId;
    if (updatedCard.id) {
      await updateNote({ noteId: updatedCard.id, userId: user?.id as string, note: updatedCard });
    } else {
      await createNote({ userId: user?.id as string, note: updatedCard });
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <Tabs
          orientation="horizontal"
          value={selectedTabs}
          onChange={handleTabChange}
          aria-label="sub-tabs"
          sx={{ paddingLeft: mobiliTheme.topLevelPadding }}
        >
          <Tab label="Patientenhinweise" value={1} />
          <Tab
            label={
              <span style={{ position: "relative" }}>
                {openTicketCount > 0 ? (
                  <Badge
                    badgeContent={openTicketCount}
                    color="error"
                    sx={{ "& .MuiBadge-badge": { transform: "translate(16px, -12px)" } }}
                  >
                    Tickets
                  </Badge>
                ) : (
                  "Tickets"
                )}
              </span>
            }
            value={2}
            sx={{ marginRight: "4px" }}
          />
          <Tab label="Verlauf" value={3} />
        </Tabs>
      </Box>

      <Card
        sx={{
          backgroundColor: mobiliTheme.cardBackground,
          height: selectedTabs === 2 ? "65vh" : "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {selectedTabs === 1 && (
          <Notes
            notes={patientNotes}
            setNotes={handleUpdateNote}
            maxVisibleNotes={patientNotes.length}
            noteKind="Patientenhinweis"
          />
        )}

        {selectedTabs === 2 && (
          <CardContent
            sx={{
              flex: 1,
              minHeight: 0,
              p: 0,
              m: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden", // IMPORTANT: no scrolling here
            }}
          >
            {/* Toggle closed tickets */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pt: 1 }}>
              <Chip
                size="small"
                icon={showClosed ? <VisibilityOffIcon /> : <VisibilityIcon />}
                label={showClosed ? "Geschlossene ausblenden" : "Geschlossene anzeigen"}
                variant={showClosed ? "filled" : "outlined"}
                onClick={() => setShowClosed(!showClosed)}
              />
            </Box>
            {/* Initial loading (first page) */}
            {!tickets && (
              <Box
                sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <CircularProgress size={28} />
              </Box>
            )}

            {/* Empty state (after load) */}
            {tickets && tickets.length === 0 && (
              <Box
                sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <Typography variant="body2">Keine Tickets gefunden</Typography>
              </Box>
            )}

            {/* Table (always render once we have any data or still can load more) */}
            {tickets && tickets.length > 0 && (
              <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
                <TicketsTable
                  scrollId="patientTicketsScroll"
                  handleRowClick={handleRowClick}
                  ticketsData={tickets}
                  hasMoreData={!!hasMore}
                  fetchMoreData={fetchNextPage}
                  isLoading={isFetchingNextPage}
                />
              </Box>
            )}
          </CardContent>
        )}

        {selectedTabs === 3 && <PatientHistoryTimeline patientId={patientId} />}

        <TicketDialog
          open={openDialog}
          handleClose={handleCloseDialog}
          ticketId={selectedTicketId}
        />
      </Card>
    </Box>
  );
});
